import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import ContactNotesThread from '../../contacts/components/ContactNotesThread';

const INTEREST = [
  { v: 'none', l: 'No interest' }, { v: 'cold', l: 'Cold' },
  { v: 'warm', l: 'Warm' }, { v: 'hot', l: 'Hot' },
];
// Same temperature palette as the agent check-in form (the "middle page").
const INTEREST_COLORS = {
  cold: { active: 'bg-[#56CCF2] border-[#56CCF2] text-white', idle: 'bg-[#EAF8FE] border-[#EAF8FE] text-[#56CCF2]' },
  warm: { active: 'bg-[#F2994A] border-[#F2994A] text-white', idle: 'bg-[#FFF4E8] border-[#FFF4E8] text-[#F2994A]' },
  hot:  { active: 'bg-[#3ACA6F] border-[#3ACA6F] text-white', idle: 'bg-[#EBF9F0] border-[#EBF9F0] text-[#3ACA6F]' },
};
const BUYER_TYPE = [
  { v: 'investor', l: 'Investor' },
  { v: 'owner_occupier', l: 'Owner occupier' },
  { v: 'developer', l: 'Developer' },
];
const FINANCE = [
  { v: 'pre_approved', l: 'Pre-approved' },
  { v: 'in_progress', l: 'In progress' },
  { v: 'cash', l: 'Cash' },
  { v: 'not_started', l: 'Not started' },
];
const SETTLEMENT = [
  { v: '30', l: '30 days' }, { v: '60', l: '60 days' },
  { v: '90', l: '90 days' }, { v: 'flexible', l: 'Flexible' },
];
const LOOKING = [
  { v: 'now', l: 'Now' }, { v: '1-3m', l: '1–3 months' },
  { v: '3-6m', l: '3–6 months' }, { v: 'just_looking', l: 'Just looking' },
];

// d/m/yyyy, the way the agency writes dates.
function enteredOn(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

/**
 * Slide-over drawer for the deeper per-buyer qualification fields the agent
 * fills in after the buyer leaves (or in a quiet moment during the OFI).
 */
export default function QualifyAttendeeDrawer({ attendee, terms, onClose, onSave, onNoteChange }) {
  // Rental open homes qualify RENTERS and never ask for a contract of sale.
  // Defaults to the buyer wording when the caller doesn't pass the terms.
  const t = terms || { noun: 'Buyer', showContractRequested: true };
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      first_name: attendee.first_name || '',
      last_name: attendee.last_name || '',
      phone: attendee.phone || '',
      email: attendee.email || '',
      interest_level: attendee.interest_level || '',
      buyer_type: attendee.buyer_type || '',
      finance_status: attendee.finance_status || '',
      bp_required: attendee.bp_required ?? null,
      settlement_preference: attendee.settlement_preference || '',
      property_to_sell: attendee.property_to_sell ?? null,
      contract_requested: attendee.contract_requested ?? null,
      solicitor_engaged: attendee.solicitor_engaged ?? null,
      price_feedback: attendee.price_feedback || '',
      looking_to_buy: attendee.looking_to_buy || '',
      other_properties_inspecting: attendee.other_properties_inspecting || '',
      // `additional_notes` is deliberately absent: notes are no longer a field
      // on this form. They live in the buyer's note thread below, where each
      // one is stamped with its author and date and nothing gets overwritten.
    });
  }, [attendee.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{attendee.full_name || attendee.name}</h3>
            <p className="text-[11px] text-slate-500">{attendee.phone || attendee.email}</p>
            {/* Who took these details at the open home — same stamp as the auto
                note written on the buyer's contact record. When there is no
                name, say why rather than showing nothing: an empty space here
                is what made the feature look broken on buyers added before it
                shipped. QR self check-ins never have an agent by design. */}
            {attendee.entered_by_name ? (
              <p className="text-[11px] text-slate-500">
                <span className="font-medium">{attendee.entered_by_name}</span> entered in details
                {attendee.check_in_time ? ` · ${enteredOn(attendee.check_in_time)}` : ''}
              </p>
            ) : attendee.checkin_method !== 'qr_self_checkin' ? (
              <p
                className="text-[11px] text-slate-400"
                title="This buyer was added before the CRM started recording who entered the details, or the agent's session had expired at the time."
              >
                Agent who entered details not recorded
              </p>
            ) : null}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-4">
          <Group title="Contact details">
            <div className="grid grid-cols-2 gap-2">
              <Input label="First name" value={form.first_name}
                onChange={(v) => set('first_name', v)} />
              <Input label="Last name" value={form.last_name}
                onChange={(v) => set('last_name', v)} />
            </div>
            <Input label="Phone" type="tel" value={form.phone}
              onChange={(v) => set('phone', v)} />
            <Input label="Email" type="email" value={form.email}
              onChange={(v) => set('email', v)} />
          </Group>

          <Group title={`${t.noun} info`}>
            {t.showContractRequested && (
              <Tri label="Contract requested" value={form.contract_requested} onChange={(v) => set('contract_requested', v)} />
            )}
            <Tri label="Has property to sell" value={form.property_to_sell} onChange={(v) => set('property_to_sell', v)} />
          </Group>

          <Group title="Interest & intent">
            <Pills label="Interest level" options={INTEREST} colors={INTEREST_COLORS}
              value={form.interest_level} onChange={(v) => set('interest_level', v)} />
            <Pills label="Buyer type" options={BUYER_TYPE}
              value={form.buyer_type} onChange={(v) => set('buyer_type', v)} />
            <Pills label="Looking to buy" options={LOOKING}
              value={form.looking_to_buy} onChange={(v) => set('looking_to_buy', v)} />
          </Group>

          <Group title="Finance & conditions">
            <Pills label="Finance status" options={FINANCE}
              value={form.finance_status} onChange={(v) => set('finance_status', v)} />
            <Tri label="Building & pest required" value={form.bp_required} onChange={(v) => set('bp_required', v)} />
            <Pills label="Preferred settlement" options={SETTLEMENT}
              value={form.settlement_preference} onChange={(v) => set('settlement_preference', v)} />
            <Tri label="Solicitor / conveyancer engaged" value={form.solicitor_engaged} onChange={(v) => set('solicitor_engaged', v)} />
          </Group>

          <Group title="Notes">
            {/* The same thread the property's Attendees tab shows, on the same
                endpoint — write a note here and it is on the property tab the
                moment it saves, and vice versa. Each note is hard-stamped with
                who wrote it and when, and adding one never overwrites the last.
                Saves on its own Save Note button, independently of this form's
                Save, so a half-filled qualification can't lose a typed note. */}
            <div className="-mx-5 border-y border-slate-200 bg-white">
              <ContactNotesThread
                identity={{
                  attendeeId: attendee.id,
                  contactId: attendee.contact,
                  email: attendee.email,
                  name: attendee.full_name || attendee.name,
                  phone: attendee.phone,
                }}
                onNoteChange={onNoteChange}
              />
            </div>
            <AutoNote attendee={attendee} />
          </Group>

          <div className="sticky bottom-0 bg-white pt-3 -mx-5 px-5 border-t flex justify-end gap-2 pb-1">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border rounded-md">Close</button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-sm bg-[#4EADB7] hover:bg-[#3f9ba4] disabled:opacity-60 text-white rounded-md">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * The system-written "who entered this buyer" note, shown in the Notes block.
 *
 * A QR self check-in has no entering agent by design, so it renders nothing. An
 * agent-device buyer with no recorded agent still shows the date, because the
 * date is half of what was asked for and an empty Notes block is what made this
 * read as broken.
 */
function AutoNote({ attendee }) {
  if (attendee.checkin_method === 'qr_self_checkin') return null;

  const date = attendee.check_in_time ? enteredOn(attendee.check_in_time) : '';
  const name = attendee.entered_by_name;
  const text = name
    ? `${name} entered in details${date ? ` - ${date}` : ''}`
    : `Entered in details${date ? ` - ${date}` : ''} (agent not recorded)`;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
        Automatic
      </div>
      <p className={`text-[12px] leading-snug ${name ? 'text-slate-700' : 'text-slate-500'}`}>
        {text}
      </p>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-2">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Pills({ label, value, options, onChange, colors }) {
  return (
    <div>
      {label && <div className="text-[11px] text-slate-500 mb-1">{label}</div>}
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = value === o.v;
          const palette = colors?.[o.v];
          let cls;
          if (palette) {
            cls = active ? palette.active : `${palette.idle} hover:opacity-80`;
          } else {
            cls = active
              ? 'bg-[#4EADB7] border-[#4EADB7] text-white'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400';
          }
          return (
            <button key={o.v} type="button" onClick={() => onChange(active ? '' : o.v)}
              className={`text-[11px] px-2.5 py-1 rounded-full border ${cls}`}>
              {o.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Tri({ label, value, onChange }) {
  // null / true / false
  const opts = [{ v: true, l: 'Yes' }, { v: false, l: 'No' }];
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-slate-500">{label}</span>
      <div className="flex gap-1.5">
        {opts.map((o) => {
          const active = value === o.v;
          return (
            <button key={String(o.v)} type="button" onClick={() => onChange(active ? null : o.v)}
              className={`text-[11px] px-2 py-0.5 rounded-full border ${active ? 'bg-[#4EADB7] border-[#4EADB7] text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
              {o.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Input({ label, type = 'text', value, onChange }) {
  // Editable contact field (name/phone/email) for fixing mistyped check-in details.
  return (
    <div>
      {label && <div className="text-[11px] text-slate-500 mb-1">{label}</div>}
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-sm" />
    </div>
  );
}
